<template>
  <b-container fluid>
    <div v-if="userObject && userObject.isSysAdmin">
      <div class="d-flex align-items-center justify-content-between mt-4 mb-3">
        <h3 class="mb-0">Elements</h3>
        <div class="d-flex align-items-center">
          <router-link to="/admin/snippets" class="mr-3">Snippets</router-link>
          <b-button variant="primary" @click="openCreateModal">
            {{ $t('buttons.create') }}
          </b-button>
        </div>
      </div>
      <admin-elements-table
        v-if="!$apollo.loading"
        :elements="elements"
        :groups="groupOptionsList"
        @edit-element="openEditModal"
        @duplicate-element="openDuplicateModal"
        @delete-element="confirmDelete"
      />
      <admin-element-modal
        :visible="modalOpen"
        :element="selectedElement"
        :duplicate-from="duplicateSource"
        :groups="groupOptionsList"
        @change="modalOpen = $event"
        @saved="onElementSaved"
      />
    </div>
    <div v-else-if="userObject" class="pt-5">
      <p class="text-center" v-html="$t('adminAccountManagement.notAllowed')"></p>
    </div>
  </b-container>
</template>

<script>
import GetAllElementsForAdmin from '@/graphQlQueries/queries/getAllElementsForAdmin';
import GetAllAccountsForAdmin from '@/graphQlQueries/queries/getAllAccountsForAdmin';
import DeleteElement from '@/graphQlQueries/mutations/deleteElement';
import SetLoadingIndicator from '@/graphQlQueries/mutations/setLoadingIndicatorInCache';
import SetPageTypeInCache from '@/graphQlQueries/mutations/setPageTypeInCache';
import SetRunBreadcrumbQueryInCache from '@/graphQlQueries/mutations/setRunBreadcrumbQueryInCache';
import SetShowHeaderInCache from '@/graphQlQueries/mutations/setShowHeaderInCache';
import UserDataAndAccess from '@/mixins/userDataAndAccess';

export default {
  name: 'AdminElements',
  mixins: [UserDataAndAccess],
  components: {
    AdminElementsTable: () => import('@/components/settings/admin/AdminElementsTable.vue'),
    AdminElementModal: () => import('@/components/settings/admin/AdminElementModal.vue'),
  },
  data: () => ({
    pageType: 'admin-settings',
    elements: [],
    modalOpen: false,
    selectedElement: null,
    duplicateSource: null,
    groupOptionsList: [],
  }),
  apollo: {
    elements: {
      query: GetAllElementsForAdmin,
      update: (data) => data.elements,
      fetchPolicy: 'network-only',
      skip() {
        return !this.userObject?.userId || !this.userObject?.isSysAdmin;
      },
    },
    groupOptionsList: {
      query: GetAllAccountsForAdmin,
      update: (data) => data.groups.map((g) => ({ group_id: g.group_id, name: g.name })),
      skip() {
        return !this.userObject?.userId || !this.userObject?.isSysAdmin;
      },
    },
  },
  watch: {
    userObject() {
      if (this.userObject && this.userObject.userId) {
        this.$apollo.mutate({
          mutation: SetLoadingIndicator,
          variables: { isIndicatorLoading: false },
        });
      }
    },
  },
  created() {
    this.$apollo.mutate({
      mutation: SetLoadingIndicator,
      variables: { isIndicatorLoading: true },
    });
    this.$apollo.mutate({
      mutation: SetPageTypeInCache,
      variables: { type: 'admin-settings' },
    });
    this.$apollo.mutate({
      mutation: SetRunBreadcrumbQueryInCache,
      variables: { runQuery: true },
    });
    this.$apollo.mutate({
      mutation: SetShowHeaderInCache,
      variables: { show: true },
    });
  },
  methods: {
    openCreateModal() {
      this.selectedElement = null;
      this.duplicateSource = null;
      this.modalOpen = true;
    },
    openEditModal(element) {
      this.selectedElement = element;
      this.duplicateSource = null;
      this.modalOpen = true;
    },
    openDuplicateModal(element) {
      this.selectedElement = null;
      this.duplicateSource = element;
      this.modalOpen = true;
    },
    onElementSaved() {
      this.$apollo.queries.elements.refetch();
    },
    async confirmDelete(element) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Delete element "${element.name}"?`,
        { okVariant: 'danger', okTitle: 'Delete' },
      );
      if (!ok) return;
      try {
        await this.$apollo.mutate({
          mutation: DeleteElement,
          variables: { elementId: element.element_id },
        });
        this.$apollo.queries.elements.refetch();
      } catch (error) {
        console.error('[AdminElements] Error deleting element:', error);
        this.$bvToast.toast('Could not delete element.', {
          variant: 'danger', solid: true, autoHideDelay: 4000,
        });
      }
    },
  },
};
</script>
